/**
 * Analytics Service - Privacy-focused page view tracking
 * 
 * Design decisions:
 * - Injected analytics ID (no hardcoded dependencies)
 * - Explicit interface (trackPageView, trackEvent)
 * - Graceful degradation when no ID provided or blocked
 */

/** Google Analytics gtag function type */
type GtagFunction = (
  command: string,
  eventNameOrConfig: string | Date,
  params?: Record<string, unknown>
) => void;

/** Analytics configuration */
interface AnalyticsConfig {
  measurementId?: string;
}

/** Event parameters */
interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: GtagFunction;
    doNotTrack?: string;
  }
}

class Analytics {
  private measurementId: string | undefined;
  private enabled: boolean;

  constructor(config: AnalyticsConfig = {}) {
    this.measurementId = config.measurementId;
    this.enabled = !!this.measurementId && this.isNotBlocked();
  }

  init(): void {
    if (!this.enabled) return;
    
    this.injectScript();
    this.configureConsent();
  }

  /**
   * Track a page view
   * @param pagePath - Optional path override (defaults to current)
   * @param pageTitle - Optional title override
   */
  trackPageView(pagePath?: string, pageTitle?: string): void {
    if (!this.enabled || !window.gtag) return;
    
    window.gtag('event', 'page_view', {
      page_path: pagePath ?? window.location.pathname,
      page_title: pageTitle ?? document.title,
      page_location: window.location.href
    });
  }

  /**
   * Track a custom event
   * @param eventName - GA4 event name
   * @param params - Event parameters
   */
  trackEvent(eventName: string, params: EventParams = {}): void {
    if (!this.enabled || !window.gtag) return;
    
    window.gtag('event', eventName, params);
  }

  private injectScript(): void {
    // Prevent duplicate injection
    if (document.querySelector('script[data-ga-script]')) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    script.setAttribute('data-ga-script', '');
    
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function(...args: Parameters<GtagFunction>) {
      window.dataLayer.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', this.measurementId as string, {
      send_page_view: false, // We'll handle page views manually for SPAs
      anonymize_ip: true,
      allow_google_signals: false, // Privacy: disable ad personalization
      allow_ad_personalization_signals: false,
      // Fix cookie domain issues on Cloudflare Pages / pages.dev
      cookie_domain: 'auto',
      cookie_flags: 'SameSite=None;Secure'
    });
  }

  private configureConsent(): void {
    if (!window.gtag) return;
    
    // Default to denied for privacy-first approach
    window.gtag('consent', 'default', {
      analytics_storage: 'granted', // We need this for basic analytics
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });
  }

  private isNotBlocked(): boolean {
    // Check if gtag is already blocked (e.g., by ad blocker)
    return typeof window.gtag !== 'undefined' || 
           window.doNotTrack !== '1';
  }
}

// Default instance with your GA4 ID
const defaultAnalytics = new Analytics({
  measurementId: 'G-Y802TF9SKR'
});

export { Analytics, defaultAnalytics };
export type { AnalyticsConfig, EventParams };
